import { WorkspaceMemberStatus } from '@affine/graphql';

import type { SignInIntent, SignInStep } from '../sign-in';
import {
  type AuthJourneyStageState,
  type AuthJourneyViewState,
  createAuthJourneyViewState,
} from './journey-manifest';

export function getSignInJourneyViewState(
  step: SignInStep,
  intent: SignInIntent = 'default',
  emailCodeSent = false
): AuthJourneyViewState {
  if (intent === 'invite') {
    return createAuthJourneyViewState(
      'workspace-invite-registration',
      emailCodeSent ? 'verify' : 'account',
      {
        invite: 'completed',
        account: emailCodeSent ? 'completed' : 'active',
        verify: emailCodeSent ? 'active' : 'pending',
        'secure-account': 'pending',
      }
    );
  }

  if (intent === 'recovery') {
    return createAuthJourneyViewState(
      'password-recovery',
      emailCodeSent ? null : 'request',
      {
        request: emailCodeSent ? 'completed' : 'active',
        reset: 'pending',
      }
    );
  }

  if (intent === 'registration') {
    return createAuthJourneyViewState(
      'registration',
      emailCodeSent ? 'verify' : 'account',
      {
        account: emailCodeSent ? 'completed' : 'active',
        verify: emailCodeSent ? 'active' : 'conditional-unresolved',
        'secure-account': 'conditional-unresolved',
        workspace: 'pending',
      }
    );
  }

  if (step === 'signInWithTotp') {
    return createAuthJourneyViewState('sign-in', 'verify', {
      authenticate: 'completed',
      verify: 'active',
      terms: 'conditional-unresolved',
    });
  }

  return createAuthJourneyViewState('sign-in', 'authenticate');
}

export function getCallbackJourneyViewState(
  kind: 'magic-link' | 'oauth-sign-in',
  requiresTwoFactor: boolean
): AuthJourneyViewState {
  if (requiresTwoFactor) {
    return createAuthJourneyViewState(kind, 'verify', {
      verify: 'active',
    });
  }
  return createAuthJourneyViewState(kind, null);
}

export function getTermsJourneyViewState(
  state: AuthJourneyStageState = 'active'
): AuthJourneyViewState {
  return createAuthJourneyViewState('terms-acceptance', 'terms', {
    terms: state,
  });
}

export function getWorkspaceSetupJourneyViewState(
  currentStageId: 'secure-account' | 'workspace',
  needsAccountSecurity: boolean
): AuthJourneyViewState {
  return createAuthJourneyViewState('workspace-setup', currentStageId, {
    'secure-account':
      currentStageId === 'secure-account'
        ? 'active'
        : needsAccountSecurity
          ? 'completed'
          : 'skipped',
    workspace: currentStageId === 'workspace' ? 'active' : 'pending',
  });
}

export function getIdentityActionJourneyViewState(
  stage: 'validate-link' | 'change' | 'destination',
  state: AuthJourneyStageState = 'active'
): AuthJourneyViewState {
  return createAuthJourneyViewState(
    'identity-action',
    stage === 'change' ? 'change' : null,
    { change: stage === 'destination' ? 'completed' : state }
  );
}

export function getPasswordRecoveryJourneyViewState(
  stage: 'request' | 'email' | 'reset'
): AuthJourneyViewState {
  const visibleStage = stage === 'email' ? null : stage;
  return createAuthJourneyViewState('password-recovery', visibleStage, {
    request: stage === 'request' ? 'active' : 'completed',
    reset: stage === 'reset' ? 'active' : 'pending',
  });
}

export function getRegistrationJourneyViewState(
  stage: 'account' | 'workspace'
): AuthJourneyViewState {
  return createAuthJourneyViewState('registration', stage, {
    account: stage === 'account' ? 'active' : 'completed',
    verify: 'skipped',
    'secure-account': 'conditional-unresolved',
    workspace: stage === 'workspace' ? 'active' : 'pending',
  });
}

export function getInviteJourneyViewState({
  authenticated,
  status,
  unavailable = false,
  requiresRegistration = false,
}: {
  authenticated: boolean;
  status?: WorkspaceMemberStatus | null;
  unavailable?: boolean;
  requiresRegistration?: boolean;
}): AuthJourneyViewState {
  if (!authenticated || requiresRegistration) {
    return createAuthJourneyViewState(
      'workspace-invite-registration',
      unavailable ? null : 'invite',
      {
        invite: unavailable ? 'error' : 'active',
        account: 'pending',
        verify: 'pending',
        'secure-account': 'pending',
      }
    );
  }
  if (unavailable) {
    return createAuthJourneyViewState('workspace-invite', null, {
      accept: 'error',
    });
  }

  const waiting =
    status === WorkspaceMemberStatus.AllocatingSeat ||
    status === WorkspaceMemberStatus.NeedMoreSeat ||
    status === WorkspaceMemberStatus.NeedMoreSeatAndReview ||
    status === WorkspaceMemberStatus.UnderReview;
  if (waiting) {
    return createAuthJourneyViewState('workspace-invite', null, {
      accept: 'completed',
    });
  }
  if (status === WorkspaceMemberStatus.Accepted) {
    return createAuthJourneyViewState('workspace-invite', null, {
      accept: 'completed',
    });
  }
  return createAuthJourneyViewState('workspace-invite', 'accept', {
    accept: 'active',
  });
}
